import React, { FunctionComponent } from "react";

import {
  ButtonsGroupChoiceField,
  FieldSpy,
  Heading,
  HideStack,
  Separator,
  SpinNumberInputField,
  Stack,
  SubmissionForm,
  SubmissionFormButtons,
  Text,
  UseFormMethods
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { SectionTitle } from "@components/SectionTitle";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  RestrictionType,
  UserDefaultAuthenticationDto
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useAddUserDefaultAuth,
  useTenantDefaultUserAuth,
  useUpdateUserDefaultAuth,
  useUserDefaultAuth
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import {
  FieldLabelWithInfo,
  mfaCountOptions,
  mfaMethodsOptions
} from "../../../_shared-blocks";
import { isEqualMfa } from "../../../_shared-blocks/network-authention-settings-section/mfa-methods-options.utils";
import { TenantAuthHint } from "./TenantAuthHint";
import { TenantAuthMfaHint } from "./TenantAuthMfaHint";
import {
  DEFAULT_MFA_COUNT_VALUE,
  UserAuthFormProps,
  UserAuthFormValues,
  userAuthOptions
} from "./user-auth-form.types";
import { getDefaultOption, getOptionValue } from "./user-auth-form.utils";

const userAuthFormValidator = new Validator<UserAuthFormValues>(validator => ({
  sessionMaxDuration: validator.number().greaterThanOrEqual(1),
  pinLifetime: validator.number().greaterThanOrEqual(1),
  deviceMfaMaxDuration: validator.number().greaterThan(1),
  mfaFirstOptions: validator.custom().predicate({
    when: (_value, values) =>
      values.mfaMethodCount === 1 || values.mfaMethodCount === 2,
    then: validator.array().required()
  }),
  mfaSecondOptions: validator.custom().predicate({
    when: (_value, values) => values.mfaMethodCount === 2,
    then: validator.array().required()
  })
}));

const UserAuthFormBase: FunctionComponent<UserAuthFormProps> = ({
  userAuth,
  tenantAuth,
  userId,
  tenantId
}) => {
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltCatalogUserAuthenticationWrite
  );

  const { mutateAsync: addUserAuth } = useAddUserDefaultAuth();

  const { mutateAsync: updateUserAuth } = useUpdateUserDefaultAuth();

  const defaultValues: UserAuthFormValues = {
    sessionMaxDuration: userAuth?.sessionMaxDuration
      ? userAuth.sessionMaxDuration.toString()
      : undefined,
    pinLifetime: userAuth?.pinLifetime
      ? userAuth.pinLifetime.toString()
      : undefined,
    quickPinRequired: getDefaultOption(userAuth?.quickPinRequired),
    mfaMethodCount: userAuth?.mfaMethodCount ?? DEFAULT_MFA_COUNT_VALUE,
    mfaFirstOptions: userAuth?.mfaFirstOptions,
    mfaSecondOptions: userAuth?.mfaSecondOptions,
    restrictionType: userAuth?.restrictionType ?? RestrictionType.Worldwide
  };

  const onSubmit = async (
    values: UserAuthFormValues,
    actions: Pick<UseFormMethods<UserAuthFormValues>, "reset" | "setValue">
  ) => {
    const isDefaultMfa = values.mfaMethodCount === DEFAULT_MFA_COUNT_VALUE;
    const dto: UserDefaultAuthenticationDto = {
      tenantId,
      userId,
      sessionMaxDuration: values.sessionMaxDuration
        ? Number(values.sessionMaxDuration)
        : undefined,
      pinLifetime: values.pinLifetime ? Number(values.pinLifetime) : undefined,
      quickPinRequired: getOptionValue(values.quickPinRequired),
      mfaMethodCount: !isDefaultMfa ? values.mfaMethodCount : undefined,
      mfaFirstOptions: !isDefaultMfa ? values.mfaFirstOptions : undefined,
      mfaSecondOptions: !isDefaultMfa ? values.mfaSecondOptions : undefined,
      restrictionType: values.restrictionType
    };

    if (userAuth) {
      await updateUserAuth(dto);
    } else {
      await addUserAuth(dto);
    }
    actions.reset(values);
  };

  const mfaOptions = mfaMethodsOptions.map(o => ({
    ...o,
    onRenderText: option => option.text
  }));

  return (
    <SubmissionForm<UserAuthFormValues>
      initialValues={defaultValues}
      onSubmit={onSubmit}
      disabled={!hasWritePermission}
      styles={{ root: { maxWidth: 1170 } }}
      validate={userAuthFormValidator.validate}
      hideButtons
      isEqual={isEqualMfa}
    >
      {(_state, actions) => {
        return (
          <>
            <Separator />
            <Stack tokens={{ childrenGap: 24 }}>
              <Stack horizontalAlign="space-between" horizontal>
                <SectionTitle>Users Login Settings</SectionTitle>
                {hasWritePermission && (
                  <SubmissionFormButtons
                    classNames={{}}
                    hideCancelButton
                    submitButtonProps={{ iconProps: { iconName: "Save" } }}
                  />
                )}
              </Stack>
              <Stack
                horizontal
                tokens={{ childrenGap: 24 }}
                horizontalAlign="space-between"
                styles={{ root: { width: "100%" } }}
              >
                <Stack
                  tokens={{ childrenGap: 16 }}
                  styles={{ root: { width: "50%" } }}
                >
                  <Heading variant="section-heading">Login Settings</Heading>
                  <SpinNumberInputField
                    onRenderLabel={() => (
                      <FieldLabelWithInfo
                        label="Session length (days)"
                        info="Maximum number of days a user can login using quick PIN / biometrics (Pin Timeout) before being prompted to login with password + MFA (MFA Session Length)"
                      />
                    )}
                    name="sessionMaxDuration"
                    min={1}
                    hint={
                      <TenantAuthHint
                        value={tenantAuth?.sessionMaxDuration}
                        defaultText="14 days"
                      />
                    }
                  />
                  <ButtonsGroupChoiceField
                    label="User quick PIN"
                    name="quickPinRequired"
                    options={userAuthOptions}
                    hint={
                      <TenantAuthHint
                        value={tenantAuth?.quickPinRequired}
                        defaultText="off"
                      />
                    }
                  />
                  <SpinNumberInputField
                    label="PIN lifetime (days)"
                    name="pinLifetime"
                    min={1}
                    hint={
                      <TenantAuthHint
                        value={tenantAuth?.pinLifetime}
                        defaultText="365 days"
                      />
                    }
                  />
                </Stack>
                <Separator vertical />
                <Stack
                  tokens={{ childrenGap: 16 }}
                  styles={{ root: { width: "50%" } }}
                >
                  <Heading variant="section-heading">
                    How Many MFA Methods for User?
                  </Heading>
                  <ButtonsGroupChoiceField<number>
                    name="mfaMethodCount"
                    options={[
                      { key: DEFAULT_MFA_COUNT_VALUE, text: "Default" },
                      ...mfaCountOptions
                    ]}
                    hint={<TenantAuthMfaHint tenantAuth={tenantAuth} />}
                  />
                  <FieldSpy<number>
                    name="mfaMethodCount"
                    onChange={value => {
                      if (value === 0) {
                        actions.setValue("mfaFirstOptions", undefined);
                        actions.setValue("mfaSecondOptions", undefined);
                      }
                      if (value === 1) {
                        actions.setValue("mfaSecondOptions", undefined);
                      }
                    }}
                  />
                  <Separator />
                  <FieldSpy<number> name="mfaMethodCount">
                    {value => {
                      if (typeof value === "undefined" || value < 0) {
                        return null;
                      }

                      return (
                        <>
                          <HideStack when={Number(value) === 0}>
                            <ButtonsGroupChoiceField<string>
                              label="MFA Step 1 Config"
                              name="mfaFirstOptions"
                              options={mfaOptions}
                              multiselect
                              required
                            />
                            <Text>
                              User must set up at least one of the selected
                              options for 2 step verification at login.
                            </Text>
                          </HideStack>
                          <HideStack
                            when={Number(value) === 0 || Number(value) === 1}
                          >
                            <Stack>
                              <ButtonsGroupChoiceField<string>
                                label="MFA Step 2 Config"
                                required
                                name="mfaSecondOptions"
                                options={mfaOptions}
                                multiselect
                              />
                              <Text>
                                User must set up a second (back-up) verification
                                method from any of the selected options.
                              </Text>
                              <Text>
                                <b>Note:</b> All MFA step 1 options should also
                                be in the MFA step 2 list
                              </Text>
                            </Stack>
                          </HideStack>
                        </>
                      );
                    }}
                  </FieldSpy>
                </Stack>
              </Stack>
            </Stack>
          </>
        );
      }}
    </SubmissionForm>
  );
};

export const UserAuthForm: FunctionComponent<Omit<
  UserAuthFormProps,
  "userAuth" | "tenantAuth"
>> = props => {
  const userAuthQuery = useUserDefaultAuth(props.tenantId, props.userId);
  const tenantAuthQuery = useTenantDefaultUserAuth(props.tenantId);
  return (
    <QueryStateIndicator
      data={{
        userAuth: userAuthQuery?.data,
        tenantAuth: tenantAuthQuery?.data
      }}
      isLoading={tenantAuthQuery?.isLoading || userAuthQuery?.isLoading}
      allowNullOrUndefined
    >
      {({ userAuth, tenantAuth }) => (
        <UserAuthFormBase
          userAuth={userAuth}
          tenantAuth={tenantAuth}
          {...props}
        />
      )}
    </QueryStateIndicator>
  );
};
